import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import {
  byElectricClearAndRecharge,
  byElectricClearRemain,
  byElectricControlRelay,
  byElectricReadMeter,
  byElectricRecharge,
  byElectricRestart,
  getMqttJsonMessages,
  getMqttJsonStatus,
  mqttJsonPublish,
} from '../../api';
import { createMqttJsonWS } from '../../utils/mqttJsonWS';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const DEFAULT_TX = 'BY/dn/141250610965';
const DEFAULT_PAYLOAD = '{\n  "header": {\n    "name": "801",\n    "ID": "141250610965"\n  },\n  "payload": {\n    "mtype": "102"\n  }\n}';
const DEFAULT_DEVICE_ID = 284330;

const MqttJsonTest = () => {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [txTopic, setTxTopic] = useState(DEFAULT_TX);
  const [payload, setPayload] = useState(DEFAULT_PAYLOAD);
  const [publishLoading, setPublishLoading] = useState(false);
  const [wsOk, setWsOk] = useState(false);
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('');
  const [deviceId, setDeviceId] = useState(DEFAULT_DEVICE_ID);
  const [rechargeValue, setRechargeValue] = useState(1);
  const [byLoading, setByLoading] = useState('');
  const [lastResult, setLastResult] = useState('');
  const wsRef = useRef(null);
  const logIdRef = useRef(0);

  const pushLog = useCallback((topic, msg, at) => {
    const id = ++logIdRef.current;
    setLogs((prev) => {
      const next = [
        {
          id,
          topic: String(topic || ''),
          message: String(msg || ''),
          at: at || new Date().toISOString(),
        },
        ...prev,
      ];
      return next.slice(0, 200);
    });
  }, []);

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await getMqttJsonStatus();
      if (res?.success) {
        setStatus(res.data || null);
      } else {
        message.error(res?.msg || '获取状态失败');
      }
    } catch (e) {
      message.error(e.message || '获取状态失败');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await getMqttJsonMessages(50);
      if (res?.success && Array.isArray(res.data?.list)) {
        const list = res.data.list;
        setLogs(
          list.map((it) => ({
            id: ++logIdRef.current,
            topic: String(it.topic || ''),
            message: String(it.message || ''),
            at: it.received_at || '',
          }))
        );
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    refreshStatus();
    loadHistory();
    const ws = createMqttJsonWS(
      (topic, msg, at) => pushLog(topic, msg, at),
      { onClose: () => setWsOk(false) }
    );
    wsRef.current = ws;
    ws.ready
      .then(() => setWsOk(true))
      .catch(() => setWsOk(false));
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [refreshStatus, loadHistory, pushLog]);

  const formatPayload = () => {
    try {
      const obj = JSON.parse(payload);
      setPayload(JSON.stringify(obj, null, 2));
      message.success('已格式化');
    } catch (e) {
      message.error('JSON 不合法：' + (e.message || ''));
    }
  };

  const doPublish = async () => {
    const topic = String(txTopic || '').trim();
    if (!topic) {
      message.warning('请填写下行主题，例如 BY/dn/{imei}');
      return;
    }
    let body = String(payload || '');
    try {
      JSON.parse(body);
    } catch (e) {
      message.error('载荷必须是合法 JSON：' + (e.message || ''));
      return;
    }
    setPublishLoading(true);
    try {
      const res = await mqttJsonPublish({ topic, payload: body, qos: 1 });
      if (res?.success) {
        message.success('已发布');
      } else {
        message.error(res?.msg || res?.error || '发布失败');
      }
    } catch (e) {
      message.error(e.response?.data?.msg || e.message || '发布失败');
    } finally {
      setPublishLoading(false);
    }
  };

  const runBY = async (key, fn) => {
    const id = Number(deviceId);
    if (!Number.isFinite(id) || id <= 0) {
      message.warning('请填写有效的 device_id');
      return;
    }
    setByLoading(key);
    setLastResult('');
    try {
      const res = await fn(id);
      const ok = !!res?.success;
      const text = JSON.stringify(res, null, 2);
      setLastResult(text);
      if (ok) {
        message.success(res?.message || '成功');
      } else {
        message.error(res?.error || res?.msg || '失败');
      }
    } catch (e) {
      const errBody = e.response?.data || { error: e.message || '请求失败' };
      setLastResult(JSON.stringify(errBody, null, 2));
      message.error(errBody.error || errBody.msg || e.message || '失败');
    } finally {
      setByLoading('');
    }
  };

  const filterKey = String(filter || '').trim().toLowerCase();
  const shown = filterKey
    ? logs.filter(
        (l) =>
          l.topic.toLowerCase().includes(filterKey) ||
          l.message.toLowerCase().includes(filterKey)
      )
    : logs;

  return (
    <div>
      <PageTitle title="通用 MQTT JSON 测试 / BY 电表" />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="独立 MQTT 账号（Client#2）：服务端订 BY/up/+；业务按钮走 /api/mqtt-json/by/*（需 device_id）。下方仍可手填 BY/dn/{imei} 发原始 JSON。"
      />

      <Card size="small" title="连接状态" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Tag color={status?.connected ? 'success' : 'error'}>
            MQTT {status?.connected ? '已连接' : '未连接'}
          </Tag>
          <Tag color={wsOk ? 'success' : 'default'}>WS {wsOk ? '已连接' : '未连接'}</Tag>
          <Text type="secondary">订阅：{status?.subscribe_topic || '—'}</Text>
          <Text type="secondary">账号：{status?.username || '—'}</Text>
          <Button size="small" loading={statusLoading} onClick={refreshStatus}>
            刷新状态
          </Button>
          <Button size="small" onClick={loadHistory}>
            拉历史缓存
          </Button>
        </Space>
      </Card>

      <Card size="small" title="BY 电表业务按钮（等回执）" style={{ marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          传 device_info.id；库中需 manufacturer=BY，MQTT 用 imei。默认示例 id=284330（按你库实际改）。
        </Paragraph>
        <Space wrap style={{ marginBottom: 12 }}>
          <span>device_id</span>
          <InputNumber
            min={1}
            value={deviceId}
            onChange={(v) => setDeviceId(v)}
            style={{ width: 140 }}
          />
          <span>充值度数</span>
          <InputNumber
            min={0}
            max={5}
            step={0.1}
            value={rechargeValue}
            onChange={(v) => setRechargeValue(v)}
            style={{ width: 100 }}
          />
        </Space>
        <Space wrap>
          <Button
            loading={byLoading === 'read'}
            onClick={() => runBY('read', (id) => byElectricReadMeter({ device_id: id }))}
          >
            抄表
          </Button>
          <Button
            loading={byLoading === 'on'}
            onClick={() =>
              runBY('on', (id) => byElectricControlRelay({ device_id: id, want_open: true }))
            }
          >
            通电
          </Button>
          <Button
            loading={byLoading === 'off'}
            onClick={() =>
              runBY('off', (id) => byElectricControlRelay({ device_id: id, want_open: false }))
            }
          >
            断电
          </Button>
          <Button
            loading={byLoading === 'recharge'}
            onClick={() =>
              runBY('recharge', (id) =>
                byElectricRecharge({ device_id: id, value: Number(rechargeValue) })
              )
            }
          >
            直接充值
          </Button>
          <Button
            loading={byLoading === 'clear'}
            onClick={() => runBY('clear', (id) => byElectricClearRemain({ device_id: id }))}
          >
            清零
          </Button>
          <Button
            type="primary"
            loading={byLoading === 'clearRecharge'}
            onClick={() =>
              runBY('clearRecharge', (id) =>
                byElectricClearAndRecharge({ device_id: id, value: Number(rechargeValue) })
              )
            }
          >
            清零并充值
          </Button>
          <Button
            danger
            loading={byLoading === 'restart'}
            onClick={() => runBY('restart', (id) => byElectricRestart({ device_id: id }))}
          >
            重启
          </Button>
        </Space>
        {lastResult ? (
          <pre
            style={{
              marginTop: 12,
              maxHeight: 220,
              overflow: 'auto',
              background: '#fafafa',
              padding: 8,
              fontSize: 12,
            }}
          >
            {lastResult}
          </pre>
        ) : null}
      </Card>

      <Card size="small" title="下发原始 JSON（调试）" style={{ marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          下行主题示例：BY/dn/厂家imei
        </Paragraph>
        <Input
          value={txTopic}
          onChange={(e) => setTxTopic(e.target.value)}
          placeholder="BY/dn/{imei}"
          style={{ marginBottom: 8 }}
        />
        <TextArea
          rows={8}
          value={payload}
          onChange={(e) => setPayload(e.target.value)}
          placeholder='{"header":{"name":"801","ID":"..."},"payload":{"mtype":"102"}}'
          style={{ fontFamily: 'Consolas, monospace', marginBottom: 8 }}
        />
        <Space>
          <Button type="primary" loading={publishLoading} onClick={doPublish}>
            发送
          </Button>
          <Button onClick={formatPayload}>格式化 JSON</Button>
        </Space>
      </Card>

      <Card
        size="small"
        title="上行监听（BY/up/+，原样 JSON）"
        extra={
          <Space>
            <Input
              allowClear
              size="small"
              placeholder="过滤 topic/内容"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: 180 }}
            />
            <Button size="small" onClick={() => setLogs([])}>
              清空
            </Button>
          </Space>
        }
      >
        <div
          style={{
            maxHeight: 420,
            overflow: 'auto',
            fontFamily: 'Consolas, monospace',
            fontSize: 12,
            background: '#fafafa',
            padding: 8,
            borderRadius: 4,
          }}
        >
          {shown.length === 0 ? (
            <Text type="secondary">暂无消息</Text>
          ) : (
            shown.map((l) => (
              <div key={l.id} style={{ marginBottom: 12, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
                <div>
                  <Text type="secondary">{l.at}</Text>
                  {'  '}
                  <Text strong>{l.topic}</Text>
                </div>
                <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {l.message}
                </pre>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
};

export default MqttJsonTest;
